import React, { useRef, useState } from 'react';
import ConfigControl, { RtdMapping } from 'components/config-control';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import useDevicesStore from 'store/devices';
import { cloneDeep } from 'lodash';
import { getConfigItem } from 'util/device';
import { Banner } from '@thermon-platforms/thermon-ui-core';
import { parseErrors } from 'util/api';


function EditModal({
  device,
  parent,
  controller,
  temperatureUnit,
  dismiss
}) {
  const scrollBody = useRef();
  const [loading, setLoading] = useState(false);
  const [deviceTemp, setDeviceTemp] = useState(cloneDeep(device));
  const [updatedConfiguration, setUpdatedConfiguration] = useState([]);
  const [, devicesStoreActions] = useDevicesStore();
  const [errors, setErrors] = useState();
  const [success, setSuccess] = useState();
  const circuitConfigItems = controller?.configuration.find(item => {
    return item.section == 'Circuit';
  })?.items || [];
  // These are handle via other mechanisms so let's ignore them
  const blockList = ['PowerState', 'LockoutTagOut'];

  // All circuit config is grouped under the circuit section
  // on the controller. So, let's manually create logical
  // groupings.
  const configuration = {};
  const secondaryConfiguration = [];

  // Dumb-dumb way to sort configuration items into groups
  circuitConfigItems.forEach(item => {
    if (!item?.index) {
      if (!configuration[item.category]) {
        configuration[item.category] = [item];
      } else {
        configuration[item.category].push(item);
      }
    } else {
      if (!secondaryConfiguration[item.index]) {
        secondaryConfiguration[item.index] = {[item.category]: [item]};
      } else {
        secondaryConfiguration[item.index][item.category] = [...(secondaryConfiguration[item.index][item.category] || []), item];
      }
    }
  });

  async function finish() {
    setLoading(true);
    setErrors(undefined);
    // Save the circuit immediately and handle side-effects
    devicesStoreActions.update({
      id: deviceTemp.id,
      name: deviceTemp.name,
      configuration: updatedConfiguration,
      type: deviceTemp.type,
    })
      .then(async updatedDevice => {
        setDeviceTemp(updatedDevice);
        setUpdatedConfiguration([]);
        // This handles showing the success banner for a short time
        setSuccess(true);
        setTimeout(() => setSuccess(false), 10 * 1000);
      })
      .catch(async res => {
        const msg = await res.json();
        setErrors(parseErrors(msg));
      })
      .finally(() => {
        scrollBody.current?.scrollTo({top: 0, behavior: 'smooth'});
        setLoading(false);
      });
  }

  /**
   * @function setValue
   * Handle setting a deeply nested configuration value
   *
   * @param {*} value
   * @param {string} key
   * @param {number} index
   * @param {string} [unit] Measurement unit associated with the value (for example, temperature unit).
   */
  function setValue(value, key, index=0, unit) {
    const item = getConfigItem({configuration: updatedConfiguration}, key, index);
    if (item) {
      item.value = value;
    } else {
      updatedConfiguration.push({key, value, deviceIndex: index, unit});
    }
    setUpdatedConfiguration([...updatedConfiguration]);
  }

  return (
    <I18nProvider i18n={i18n}>
      <header>
        <h3><Trans>Configure</Trans> {deviceTemp.type}: <strong>{getConfigItem(deviceTemp, 'Name')?.value}</strong></h3>
      </header>
      <section ref={scrollBody}>
        <Banner show={errors} status="alert" small={true}>
          <Trans>There was an issue saving this circuit.</Trans>
        </Banner>
        <Banner show={success} status="ok" small={true}>
          <Trans>Settings changes have been requested.</Trans>
        </Banner>
        {/* Device Configuration Section */}
        {Object.keys(configuration) // Grab section keys
          .sort((a, b) => configuration[a][0].categoryOrder - configuration[b][0].categoryOrder) // Sort using category order found on items (for some reason)
          .map(sectionKey => {
            const section = configuration[sectionKey].sort((a, b) => a.displayOrder - b.displayOrder);
            if (!section.length) return;
            // If a section only contains name, hide it since we handle name above
            if (section.length == 1 && section[0].key == 'Name') return null;
            return (
              <div className="configuration-section" key={sectionKey}>
                <h4>{sectionKey}</h4>
                <ul className="form">
                  {section.map((item, i) => {
                    if (blockList.includes(item.key)) return null;
                    // NOTE - This is an ugly hack to append the correct temperature unit
                    if (item?.unit?.startsWith('°')) {
                      item = Object.assign({}, item, {unit: temperatureUnit ? '°' + temperatureUnit : item.unit});
                    }// / hack
                    return (
                      <li key={`config-${sectionKey}-${i}`}>
                        {item.key == 'ControlSensorsMap' ?
                          <RtdMapping
                            controller={parent}
                            item={item}
                            error={errors?.[item.key]}
                            onChange={val => setValue(val, item.key)}
                            value={getConfigItem(deviceTemp, item.key)}
                          /> :
                          <ConfigControl
                            item={item}
                            error={errors?.[item.key]}
                            onChange={val => setValue(val, item.key, 0, item?.unit)}
                            value={getConfigItem(deviceTemp, item.key)}
                          />
                        }
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }
        )}
        {/* Secondary Configuration Section */}
        {secondaryConfiguration.map((configuration, index) => {
          return (<React.Fragment key={`secondary-config-${index}`}>
            {Object.keys(configuration).map(sectionKey => {
              const section = configuration[sectionKey].sort((a, b) => a.displayOrder - b.displayOrder);
              if (!section.length) return null;
              return (
                <div className="configuration-section" key={sectionKey}>
                  <h4>{sectionKey}</h4>
                  <ul className="form">
                    {section.map((item, i) => {
                      // NOTE - This is an ugly hack to append the correct temperature unit
                      if (item?.unit?.startsWith('°')) {
                        item = Object.assign({}, item, {unit: temperatureUnit ? '°' + temperatureUnit : item.unit});
                      }// / hack
                      return (
                        <li key={`config-${sectionKey}-${i}`}>
                          {item.key == 'ControlSensorsMap' ?
                            <RtdMapping
                              controller={parent}
                              item={item}
                              error={errors?.[item.key]}
                              onChange={val => setValue(val, item.key, index)}
                              value={getConfigItem(deviceTemp, item.key, index)}
                            /> :
                            <ConfigControl
                              item={item}
                              error={errors?.[item.key]}
                              onChange={val => setValue(val, item.key, index)}
                              value={getConfigItem(deviceTemp, item.key, index, item?.unit)}
                            />
                          }
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </React.Fragment>);
        })}
      </section>
      <footer>
        <button onClick={dismiss}>
          <Trans>Close</Trans>
        </button>
        <button
          className={`primary ${loading ? 'processing' : ''}`}
          onClick={finish}
          disabled={loading || updatedConfiguration.length == 0}
        >
          <Trans>Save</Trans>
        </button>
      </footer>
    </I18nProvider>
  );
}

export default EditModal;
